/**
 * Constants for the global entity search (Cmd + K / Ctrl + K).
 */

// Entity kinds the search can return.
const ENTITY_TYPES = {
  DISTRIBUTION: 'distribution',
  FARMING_UNIT: 'farming_unit',
  FIELD: 'field',
  SENSOR: 'sensor',
};

// When two results match equally well, the broader container wins.
const ENTITY_TYPE_ORDER = {
  [ENTITY_TYPES.DISTRIBUTION]: 0,
  [ENTITY_TYPES.FARMING_UNIT]: 1,
  [ENTITY_TYPES.FIELD]: 2,
  [ENTITY_TYPES.SENSOR]: 3,
};

// i18n keys for the badge rendered next to every result.
const ENTITY_TYPE_LABEL_KEYS = {
  [ENTITY_TYPES.DISTRIBUTION]: 'distribution',
  [ENTITY_TYPES.FARMING_UNIT]: 'farming_unit',
  [ENTITY_TYPES.FIELD]: 'field',
  [ENTITY_TYPES.SENSOR]: 'sensor',
};

// Sensor product names, mirroring what the field sensor card shows
// (app/components/dashboard/field/Sensor/Sensor.jsx). Brand names, so they are
// deliberately not translated — same as there. If Sensor.jsx is ever
// refactored, these two maps should move somewhere both can share.
const SENSOR_TYPE_LABELS = {
  wp: 'N-Drip Tensiometer',
  pulse: 'N-Drip Pulse',
  piezometer: 'N-Drip Piezometer',
  soil_temp: 'Soil Temp Sensor',
  high_pressure: 'N-Drip High Pressure',
  low_pressure: 'N-Drip Low Pressure',
};

// Flow meters (type 'wfr') are named by their manufacturer instead.
const SENSOR_MANUFACTURER_LABELS = {
  motorola: 'Motorola',
  ndrip: 'N-Drip Flow Meter',
  galcon: 'Galcon',
  aquastride: 'Aquastride',
  'tal-gil': 'Tal Gil',
  soil_temp: 'Soil Temp Sensor',
  'ndrip-ai': 'AI Irrigator',
};

// Archived fields (and their sensors) stay out of the index: the dashboard
// keeps its archive toggle in component state, so jumping straight to an
// archived field would leave the field list and the field view out of sync.
const ARCHIVED_FIELD_STATE = 'archive';

// Match quality of one query token against one searchable string.
// Lower is better. NO_MATCH is tested explicitly, never sorted on.
const MATCH_RANK = {
  EXACT: 0,
  PREFIX: 1,
  WORD_PREFIX: 2,
  SUBSTRING: 3,
  NO_MATCH: 99,
};

// Characters that start a new "word" inside a name or an id.
const WORD_SEPARATORS = [' ', '-', '_', '.', '/', ':'];

// How many recently visited fields the search offers when the box is empty.
const MAX_RECENT_FIELDS = 5;

// sessionStorage key for those visits. sessionStorage, not localStorage: the
// list is explicitly "this session" — it survives a reload of the tab and dies
// with it, so a user does not come back tomorrow to yesterday's trail.
const RECENT_FIELDS_STORAGE_KEY = 'global_search_recent_fields';

// Which list a chosen row came from. Reported on every selection so the
// recents can be judged against the search itself.
const RESULT_SOURCE = {
  SEARCH: 'search',
  RECENT: 'recent',
};

// Upper bound on rendered rows. Matches beyond it are counted, not rendered,
// so a two-letter query over thousands of sensors stays cheap to paint.
const MAX_RESULTS = 50;

// Rollout gate: entirely server-side, via a Remote Configuration of this name.
// GET /remote_user_configurations resolves it per caller —
// users_email[email] > environments[env] > default_value — and returns the
// winner as `default_value`, so the audience is changed from the Remote
// Configurations page with no deploy.
const GLOBAL_SEARCH_CONFIG_NAME = 'global_search';

// Field View has its own route (N-281); '/' only redirects to Farm View.
const { FIELD_VIEW_PATH, FARM_VIEW_PATH } = require('../viewPaths');
// Routes that already show a distribution / farming unit, so picking one of
// those does not need to move the user anywhere.
const MAIN_SCREEN_PATHS = [
  FIELD_VIEW_PATH,
  FARM_VIEW_PATH,
  '/Planning',
  '/Insights',
];

module.exports = {
  ENTITY_TYPES,
  ENTITY_TYPE_ORDER,
  ENTITY_TYPE_LABEL_KEYS,
  SENSOR_TYPE_LABELS,
  SENSOR_MANUFACTURER_LABELS,
  ARCHIVED_FIELD_STATE,
  MATCH_RANK,
  WORD_SEPARATORS,
  MAX_RESULTS,
  MAX_RECENT_FIELDS,
  RECENT_FIELDS_STORAGE_KEY,
  RESULT_SOURCE,
  GLOBAL_SEARCH_CONFIG_NAME,
  FIELD_VIEW_PATH,
  FARM_VIEW_PATH,
  MAIN_SCREEN_PATHS,
};
